import React, { useEffect, useState, useContext } from "react"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { usePermission } from "../../utils/hasPermission"

import { Layout, Button, message, DrawerProps } from "antd"
import TableExtended, {
  ITableExtended,
} from "../../components/table/TableExtended"
import { useTranslation } from "react-i18next"
import { Search } from "react-feather"
import SEO from "../../containers/helpers/SEO"
import useQueryParams from "../../hooks/useQueryParams"
import Modal from "../../components/modal"
import getNodes from "../../utils/getNodes"
import capitalize from "../../utils/capitalize"
import { FunctionComponent } from "react"
import { ReactElement } from "react"
import Drawer from "../../components/drawer"
import usePagination from "../../hooks/usePagination"
import { PLACEHOLDER_MUTATION } from "../../rest-api/mutations/types/placeholder"
import { IColumnType } from "../../types"
import { periodInterval } from "../../utils/defaultPeriod"   // Tog'a #438: sukut «Сегодня»

import { useMutation, useQuery } from "@tanstack/react-query"
export interface IPage {
  name: string
  singularName?: string
  collectionQuery: any
  exportQuery?: any
  deleteBulkMutation?: any
  importMutation?: any
  where?: object
  formComponent?: ReactElement
  formWidth?: number | string   // Boshliq 2026-09-07: tovar jadvalli formalar uchun keng modal (standart 520px siqilardi)
  filterComponent?: FunctionComponent<any>
  filterComponentProps?: any
  columns: IColumnType[]
  sampleData?: any
  importTypes?: any
  tableProps?: Partial<ITableExtended>
  drawerProps?: DrawerProps
  triggerImport?: (cp: Function, data: any) => void
  restructureData?: (record: any[]) => any[]
  filterColumn?: (record: any) => void
  namedFilter?: boolean
  isHasTitle?: boolean
  queryVariables?: any
  type?: number
  defaultWeeklyFilter?: boolean
  excelDetail?: any
  setIsSelectedRows?: React.Dispatch<React.SetStateAction<any[]>>
  trash?: boolean
  disableRowClick?: boolean
  hideCreate?: boolean
  hideDelete?: boolean
  hideImport?: boolean
}
export interface ICollectionNode {
  id: number
}

function Page(props: IPage) {
  const title = props.singularName || props.name.slice(0, -1)
  const { t, i18n } = useTranslation()

  const [drawerTitle, setDrawerTitle] = useState(t(`add${capitalize(title)}`))
  const query = useQueryParams()
  // tog'a #3410 (2026-10-04): mavjud yozuv kartasi (URL da id bor) ham «Добавить …» sarlavhasi bilan ochilardi. Endi «edit…» kalit
  // bo'lsa — u; bo'lmasa «Добавить X» → «Редактировать X» (uz: «… qo'shish» → «… tahrirlash», en: «Add» → «Edit»).
  const editTitle = (() => {
    const cap = capitalize(title)
    if (i18n.exists(`edit${cap}`)) return t(`edit${cap}`)
    return String(t(`add${cap}`)).replace(/^Добавить/, "Редактировать").replace(/^Add/, "Edit").replace(/qo['ʻ‘’]shish$/i, "tahrirlash").replace(/^Qo['ʻ‘’]shish/, "Tahrirlash")
  })()
  const shownTitle = query.secureGet("id") ? editTitle : drawerTitle
  const { pageConfig } = usePagination(props.name)

  const { authState } = useContext(AuthContext)
  const permissions = usePermission(authState.user, props.name)

  const {
    isLoading: loading,
    error,
    data,
    refetch,
  } = useQuery(
    [
      props.name,
      ...Object.values({
        ...pageConfig,
        ...(props.queryVariables && props.queryVariables),
        ...(props.where && props.where),
      }),
    ],
    () =>
      props.collectionQuery({
        ...pageConfig,
        ...(props.queryVariables && props.queryVariables),
        ...(props.where && props.where),
      }),
    {
      onSuccess: (data) => {},
      // audit F4 (2026-09-04): xom axios matni («Request failed with status code 404») ikkinchi toast bo'lib chiqardi —
      // sababli xabarni utils/config.ts allaqachon ko'rsatadi (403 GET esa jim bo'lishi kerak)
      onError: () => {},
    }
  )
  const {
    data: allData,
    refetch: refetchAllData,
    isFetching: loadingAllData,   // tog'a #309: o'chiq so'rovda react-query v4 isLoading doim true — eksport tugmasi «Загрузка…» da qolardi
  } = useQuery<any>(
    [
      "all_products",
      ...Object.values({
        ...(props.queryVariables && props.queryVariables),
        ...(props.where && props.where),
      }),
    ],
    () =>
      props.exportQuery
        ? props.exportQuery({
            ...(props.queryVariables && props.queryVariables),
            ...(props.where && props.where),
          })
        : Promise.resolve(null),
    {
      // tog'a #309 (2026-10-20, jonli o'lchov): «hamma yozuvlar» (PageSize=10000000) HAR sahifa ochilishida, sana filtri qo'shilgach
      // yana bir marta yuklanardi — «Деньги» da sahifaning eng sekin 2 so'rovi; ma'lumot ko'paysa sahifa sekinlashadi. U faqat
      // «Итого» uchun kerak, server jamini (totalOutcome) bermagan bo'lsa; eksport o'zi yangidan so'raydi (handleExport).
      enabled: !!props.exportQuery && !!data && !(data as any)?.totalOutcome,
    }
  )

  const { mutateAsync: importDataAsync, isLoading: iLoading } = useMutation(
    props.importMutation || PLACEHOLDER_MUTATION,
    {
      onSuccess: (data) => {
        message.success(t("imported"))
        query.remove("fillingForm")
      },
      onError: (err) => {
        message.error((err as any)?.response?.data?.message || (err as any)?.message || String(err))   // #2065 S1: server xabari (masalan artikul takrori), «Error: Request failed…» emas
      },
    }
  )


  function handleAdd() {
    setDrawerTitle(t(`add${capitalize(title)}`))
    query.set(title, "add")
  }

  async function handleImport(arr: any) {
    if (!arr?.length) return

    const data = arr.map((x) => {
      const obj: any = {}
      Object.keys(x).forEach((k) => {
        if (props.importTypes?.[k]) {
          obj[k] = props.importTypes[k](x[k] || null)
        }
      })

      return obj
    })

    // tog'a #2892: natija (qabul qilindi / qilinmadi) chaqiruvchiga qaytariladi — FillingForm hisobot ko'rsatadi
    let natija: any = undefined
    try {
      natija = await importDataAsync(
      data?.filter((x: any) => {
        const values = Object.values(x)
        // Kamida bitta qiymat bo'lishi kerak (butunlay bo'sh qatorlarni o'chirish)
        return values.some((y) => y !== null && y !== undefined && y !== "null" && y !== "")
      })
      )
    } catch {
      // xato xabarini useMutation.onError allaqachon ko'rsatdi
    }

    refetch()
    return natija
  }

  function handleRowClick(row: any, index: number | undefined) {
    row.id &&
      query.append({
        [title]: "add",
        id: row.id,
      })
  }

  const leftButtons: JSX.Element[] = [
    <Button
      key="filter"
      size="large"
      style={{ width: "200px" }}
      className="table-action-btn headermargin_r"
      icon={<Search key="search" />}
      onClick={() => query.set("filtering", "true")}
    >
      {t("filter")}
    </Button>,
  ]

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.get(title), pageConfig])

  useEffect(() => {
    if (error && (error as any).graphQLErrors?.length) {
      data?.graphQLErrors?.forEach((err: any) => {
        message.error(
          err?.extensions?.message ? t(err.extensions.message) : err.message
        )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, data])

  useEffect(() => {
    if (!query.has("begin") && props.defaultWeeklyFilter) {
      // Tog'a #438: sukut — «Сегодня» (prop nomi eski: defaultWeeklyFilter)
      const { begin, end } = periodInterval("today")
      query.append({
        begin,
        end,
      })
    }
    // tog'a #1914 (2026-09-14): Orders/updateOrdersMCount endpointi 2026-09-02 da olib tashlangan (yagona Chat API) — chaqiruv 405 berardi, olib tashlandi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.name, +query.secureGet("chatId")])

  const idColumn: IColumnType = {
    title: "ID",
    dataIndex: "id",
    align: "center",
    sorter: true,
    defaultSortOrder: "descend",
    sortDirections: ["ascend"],
  }

  return (
    <div className="PageTable_r">
      <Layout className={`${props.name}-page`}>
        <SEO title={t(props.name)} />
        <TableExtended
          name={title}
          titleName={props.name}
          isHasTitle={props.isHasTitle ?? false}
          setIsSelectedRows={props.setIsSelectedRows}
          type={props.type}
          onRow={
            props.trash || props.disableRowClick || !permissions.canEdit   // Boshliq «Menejer huquqlari» (2026-09-14): tahrir huquqi yo'q — forma ochilmaydi
              ? undefined
              : (data: any, index) => ({
                  // ⛔ tog'a #2355 (2026-09-22): PLANSHETDA qator ichidagi havola/tugmaga tegilganda
                  // «Добавить» oynasi ochilib ketardi — sensorli ekranda tegish qator ustidagi
                  // ikki marta bosishga tushadi. Endi hodisa INTERAKTIV elementdan kelgan bo'lsa
                  // (havola, tugma, maydon, tanlagich, oyna ichi) forma ochilmaydi.
                  // ⚠️ `.ant-tag` ATAYIN ro'yxatda YO'Q: teg — oddiy belgi, uning ustida ikki marta
                  // bosib tahrirga kirish odati buzilmasin (faqat haqiqiy interaktiv elementlar to'siladi).
                  onDoubleClick: (evt: any) => {
                    const t = evt?.target as HTMLElement | undefined
                    if (t && typeof t.closest === "function" &&
                        t.closest("a,button,input,textarea,label,.ant-select,.ant-picker,.ant-modal,.ant-dropdown,.ant-checkbox-wrapper,.ant-input-number"))
                      return
                    handleRowClick(data, index)
                  },
                })
          }
          loading={loading || iLoading}
          loadingAllData={loadingAllData}
          allData={allData?.data}
          sampleData={props.sampleData}
          excelDetail={props.excelDetail}
          deleteBulkMutation={props.deleteBulkMutation}
          refetch={refetch}
          refetchAllData={refetchAllData}
          onAdd={handleAdd}
          onImport={(data) =>
            props.triggerImport
              ? props.triggerImport(handleImport, data)
              : handleImport(data)
          }
          dataSource={
            props.restructureData
              ? props.restructureData(getNodes(data?.data, "id"))
              : getNodes(data?.data, "id")
          }
          columns={[idColumn, ...props.columns]}
          leftButtons={leftButtons}
          filterMatcher={props.namedFilter ? `${props.name}filtering` : "filtering"}
          hasFilter={!!props.filterComponent}
          pageInfo={data}
          onSort={(sort) =>
            query.setObj(props.name + "orderBy", { [sort.field]: sort.order })
          }
          hideAdd={props.hideCreate || !permissions.canCreate}
          hideDelete={props.hideDelete || !permissions.canDelete}
          hideImport={props.hideImport || !permissions.canCreate}
          {...props.tableProps}
        />
        {props.drawerProps ? (
          <Drawer
            removeParamsOnClose={["id"]}
            matcher={title}
            title={shownTitle}
            {...props.drawerProps}
          >
            {props.formComponent}
          </Drawer>
        ) : (
          <Modal
            removeParamsOnClose={["id"]}
            footer={null}
            matcher={title}
            title={shownTitle}
            width={props.formWidth}
            className={props.formWidth ? "form-modal-wide" : undefined}
          >
            {props.formComponent}
          </Modal>
        )}

        {props.filterComponent && (
          <props.filterComponent
            namedFilter={props.namedFilter}
            name={props.name}
            matcher={props.namedFilter ? `${props.name}filtering` : "filtering"}
            {...props.filterComponentProps}
          />
        )}
      </Layout>
    </div>
  )
}

export default React.memo(Page)
